/* The counterparty card's Orders tab: the counterparty's orders from the last 90 days, newest first,
   in the shared embedded `DocsTab` (paging, error + retry, first-load skeleton), and the order
   modal. Its section heading (`ReadOnlyTag`, `TabHead`) is `tabParts.js`, shared with Returns.

   ⚠ The window is `date_from` alone — today minus 89 days, the BROWSER's local date, so today makes
   the 90th day. No `date_to`: nothing is dated in the future, and the API's own `-datetime` default
   is the "newest first". The server compares `datetime__date` in its own time zone, so an order
   placed around midnight on the boundary day can fall either side of it.

   A row opens the same modal `/sales/orders` does (`orders/OrderModal.js`, same GET) without
   touching the URL. Every link in a row is a real link (it opens in a new tab like any other) and
   stops its own click from reaching the row. Every way OUT of the card — those links, the modal's
   "Open page" and its visit link, "All orders" — goes through the card's `leaveTo`, which asks
   before unsaved edits in the Overview form are thrown away.

   The order's page is handed the card's own address (`cardLocation`: this tab, plus the card's
   route state, which is what keeps its breadcrumb pointing at the filtered counterparty list) as
   `state.from`, so its Back returns here. */

import React, { useState } from "react";

import { StatusIcon, dash, statusColumn, textOf } from "../../component/table-cells";
import { COUNTERPARTIES_PAGE, ORDERS_PAGE } from "../../config/lang";
import dayjs from "../../util/dayjs";
import { formatMoney } from "../../util/formatter";
import { translate } from "../../util/util";
import OrderModal from "../orders/OrderModal";
import { STATUS_ICON } from "../orders/parts";

import DocsTab from "./DocsTab";
import { AuthorCell, NumberCell, PointCell, VisitCell } from "./tabParts";

const WINDOW_DAYS = 90;

export default function OrdersTab({ counterpartyId, lang, cardLocation, leaveTo }) {
  const t = key => translate(COUNTERPARTIES_PAGE, key, lang);
  const [modalId, setModalId] = useState(null);

  const dateFrom = dayjs()
    .subtract(WINDOW_DAYS - 1, "day")
    .format("YYYY-MM-DD");
  const orderPage = id => ({ pathname: `/sales/orders/${id}`, state: { from: cardLocation } });

  /* The columns of `/sales/orders` (`orders/List.js`), cut down to the ones that mean something on
     this counterparty's card: the same cells, alignment and pinned leading pair, and the same
     content-sized layout (`lp-fit`, `scroll.x: "max-content"`) — the text columns take what their
     content needs and share spare room in proportion to it; the status icon and the two numbers
     keep the list's pixel widths. Below the content's width the table scrolls inside the card.
     The one difference is that the number, the point and the author are LINKS here (to the order's
     page, the point's card, the author's profile), so they carry the link colour; the list's row
     menu has no room on a card, and its own cells are plain text. */
  const columns = [
    statusColumn((v, row) => {
      const { Icon, tone } = STATUS_ICON[row.status] || STATUS_ICON.new;
      const label = translate(ORDERS_PAGE, `order_status_${row.status}`, lang) || row.status;
      return <StatusIcon Icon={Icon} tone={tone} label={label} />;
    }),
    {
      /* The list's `TitleCell`: the number on top, the date and time under it. */
      key: "name",
      title: t("o_number_date"),
      fixed: "left",
      render: (v, row) => <NumberCell row={row} to={orderPage(row.id)} leaveTo={leaveTo} />,
    },
    { key: "point", title: t("o_point"), render: (v, row) => <PointCell row={row} leaveTo={leaveTo} /> },
    { key: "created_by", title: t("o_author"), render: (v, row) => <AuthorCell row={row} leaveTo={leaveTo} /> },
    { key: "visit", title: t("o_visit"), render: (v, row) => <VisitCell row={row} lang={lang} leaveTo={leaveTo} /> },
    {
      /* `items_count`: the API's `Count("items")` over the order's lines (the list queryset's own
         annotation) — never derived from `order_total`, which holds the line count on some rows
         and money on others (CLAUDE.md, the money note). Right-aligned and 100px, as the list's. */
      key: "items_count",
      title: t("r_items"),
      width: 100,
      align: "right",
      render: (v, row) => <span className="lp-num">{textOf(row.items_count)}</span>,
    },
    {
      /* `total` — the final sum, as `/sales/orders` shows it — never `order_total` (the money note
         again). No totals row, for the same reason. Right-aligned and 130px, as the list's. */
      key: "total",
      title: t("o_sum"),
      width: 130,
      align: "right",
      render: (v, row) => <span className="lp-num">{formatMoney(row.total) || dash}</span>,
    },
  ];

  const navigate = to => leaveTo(null, to);

  return (
    <>
      <DocsTab
        counterpartyId={counterpartyId}
        endpoint="excalibur/orders/"
        detailPath="/sales/orders"
        columns={columns}
        emptyText={t("orders_empty")}
        lang={lang}
        query={{ date_from: dateFrom }}
        onRowClick={row => setModalId(row.id)}
        /* Content-sized, as the list (see above). */
      />
      <OrderModal id={modalId} lang={lang} onClose={() => setModalId(null)} openTo={modalId ? orderPage(modalId) : null} navigate={navigate} hideCounterparty />
    </>
  );
}
